
import {KYCGeneralStatus, LocalLanguage} from "@/types_and_interfaces/types";
import {useTranslation} from "react-i18next";
import moment from 'moment';
import CommonClass from "@/utils/common.ts";
import 'moment/locale/ro';
import 'moment/locale/ru';
import {isMobile} from "react-device-detect";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import DashboardClass from "@/utils/dashboard_class.ts";
import {get_market_qr_codes, user_update} from "@/services/account.ts";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {register_device} from "@/utils/firebase_init.ts";
import * as Sentry from "@sentry/react";


function useCommon() {
    // STATES
    
    const {t,  i18n } = useTranslation();
    
    const currentLanguage = i18n.language;
    
    const setErrTrue = new CustomEvent(CommonClass.CustomEvents.set_err, {
        detail: {
            value: true,
        }
    });
    
    const setErrFalse = new CustomEvent(CommonClass.CustomEvents.set_err, {
        detail: {
            value: false,
        }
    });
    
    const setLoadTrue = new CustomEvent(CommonClass.CustomEvents.set_req_load, {
        detail: {
            value: true,
        }
    });
    
    const setLoadFalse = new CustomEvent(CommonClass.CustomEvents.set_req_load, {
        detail: {
            value: false,
        }
    });
    
    const { executeRecaptcha } = useGoogleReCaptcha();
    
    
    const handleEmailTo = (email:string) => {
        if(!window || !email) return
        window.location.href = `mailto:${email}`;
    };
    
    const handleTelTo = (tel:string) => {
        if(!window) return
        if(isMobile) {
            window.location.href = `tel:${tel}`;
        } else {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "info", t("copied"))
            window.navigator.clipboard.writeText(tel)
        }
    };
    
    const copyToClipboard = (x:string) => {
        if(x.length) {
            window.navigator.clipboard.writeText(x)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "info",  t("copied"))
        }
    }
    
    const smartNavigation = (url:string) => {
        if(url.length) {
            if(url.includes("http")){
                window.open(url, "_blank")
            } else {
                eventEmitter.emit(CommonClass.CustomEvents.redirect_to, url)
            }
        }
    }

    const firebase_init = (ln:string) => {
        if ("serviceWorker" in navigator) {

            import('@/utils/firebase_init.ts')
                .then((firebaseUtils) => {

                    firebaseUtils.requestPermission(ln);
                    const unsubscribe = firebaseUtils.onMessageListener().then((_payload: any) => {
                        console.log("Message received. ", _payload);
                    });

                    return () => {
                        unsubscribe.catch((err) => Sentry.captureException(err, { tags: { section: "firebase", action: "message_listener" } }));
                    };
                })
                .catch((error) => {
                    Sentry.captureException(error, { tags: { section: "firebase", action: "init" } });
                });
        } else {
            console.warn("Firebase messaging is not supported in this browser or context (possibly incognito mode).");
        }
    }
    
    const setBgGray = (set:boolean) => {
        
        
        if(typeof document.body !== "undefined" ) {
            
            if(set) {
                document.body.classList.add("gray_bg")

            } else {
                document.body.classList.remove("gray_bg")
            }
        }
    }
    
    const setBgSmoothGreen = (set:boolean) => {
        
        
        if(typeof document.body !== "undefined" ) {
            
            if(set) {
                document.body.classList.add("smooth_green_bg")

            } else {
                document.body.classList.remove("smooth_green_bg")
            }
        }
    }
    
    const addOverflow = (set:boolean) => {
        if(typeof document.body !== "undefined" ) {
            
            if(set) {
                document.body.classList.add("no_overflow")
                
            } else {
                document.body.classList.remove("no_overflow")
            }
        }
    }
    
    const deletePageBottomPadding = (set: boolean) => {
        const page = document.getElementById("page_container");
        
        if(page) {
            if(set) {
                page.classList.add("no_padding_bottom")
            } else {
                page.classList.remove("no_padding_bottom")
            }
        }
    }
    
    const langToggler = () => {
        const ln = currentLanguage === "ro" ? "ru" : "ro"
        i18n.changeLanguage(ln);
        register_device(ln)

    }
    
    const setSpecificLanguage = (lan: LocalLanguage) => {
        i18n.changeLanguage(lan);
        // requestPermission(lan)

        if ("serviceWorker" in navigator) {

            import('@/utils/firebase_init.ts')
                .then((firebaseUtils) => {
                    firebaseUtils.requestPermission(lan);
                })
                .catch((error) => {
                    Sentry.captureException(error, { tags: { section: "firebase", action: "set_language" } });
                });
        } else {
            console.warn("Firebase messaging is not supported in this browser or context (possibly incognito mode).");
        }
    }
    
    const formatMomentDate = (date_text:string) => {
        const hasMidnightUTC =  isJSDateToStringFormat(date_text) || date_text?.length === 10 || date_text.endsWith("T00:00:00Z");
        const date = moment(new Date(date_text)).locale(i18n.language);

        return  isDDMMYYYYFormat(date_text)
            ? date_text
            : hasMidnightUTC
                ? date.format('DD.MM.YYYY')
                : date.format('DD.MM.YYYY HH:mm:ss') || ""
    }

    const isJSDateToStringFormat = (str: string)=> {
        const pattern = /^[A-Z][a-z]{2} [A-Z][a-z]{2} \d{2} \d{4} \d{2}:\d{2}:\d{2} GMT[+-]\d{4} \(.+\)$/;
        return pattern.test(str);
    }

    const isDDMMYYYYFormat = (str: string)=> {
        const pattern = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.\d{4}$/;
        return pattern.test(str);
    }

    const filterAlphaPinField = (e:any) => {
        let v = e.target.value
        let res = true
        
        if(typeof v === "string" && v.length) {
            v.split("").map((ch:string) => {
                if(Number(ch) || ch === "0") {
                    res = true
                } else {
                    res = false
                }
            })
            
        }
        
        if(!res) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("pin_condition1"))
        }
    
    }

    const kyc_middleware = (status:KYCGeneralStatus | undefined) => {
        
        let enable_next:boolean = false
        
        if(status === "Unknown" || status === "UserFinished" || status === "ProcessingFailed" || status === "UserStarted"){
            eventEmitter.emit(CommonClass.CustomEvents.redirect_to, DashboardClass.dashboard_routes.KYC)
            enable_next = false
        } else if(status === "ProcessingFailedAndSentToManager"){
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("failed_kyc1"))
            eventEmitter.emit(CommonClass.CustomEvents.redirect_to, DashboardClass.dashboard_routes.Home)
            enable_next = false
        } else if(status === "ProcessingSuccess") {
            enable_next = true
        } else if(status === "ServerProcessing") {
            eventEmitter.emit(CommonClass.CustomEvents.redirect_to, DashboardClass.dashboard_routes.KYCWaitingRoom)
            enable_next = false
        } else enable_next = false
        
        return enable_next
    }
    
    const getMarketsQrCOdes = async () => {
        let r_token:string | undefined  = undefined
        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }
        const res = await get_market_qr_codes(r_token)
        
        if(res.status === 200 && res.response.data){
            return res.response.data
        } else return {}
    }
    
    const getRecaptchaToken = async () => {
        try {
            if(CommonClass.general_settings.recaptcha.is_enabled && executeRecaptcha) {
                return await executeRecaptcha('homepage');
            } else {
                return undefined
            }
            
        } catch (e) {
            Sentry.captureException(e, { tags: { section: "recaptcha", action: "get_token_common" } });
            return undefined
        }
    }
    
    const removeScrollability = (open:boolean) => {
        if(open) {
            
            const root =  document.getElementsByTagName('html')[0];
            root.setAttribute('style', 'overflow-y: hidden');

        } else {
            const root =  document.getElementsByTagName('html')[0];
            root.removeAttribute('style');
        }
    }

    const getUserUpdate = async () => {

        const res = await user_update()

        if(res.status === 200 && res.response.data){
            return res.response.data
        } else return {}
    }
    
    return {
        handleEmailTo,
        handleTelTo,
        copyToClipboard,
        setBgGray,
        deletePageBottomPadding,
        langToggler,
        setSpecificLanguage,
        formatMomentDate,
        setBgSmoothGreen,
        addOverflow,
        filterAlphaPinField,
        smartNavigation,
        setErrTrue,
        setErrFalse,
        setLoadTrue,
        setLoadFalse,
        kyc_middleware,
        getMarketsQrCOdes,
        firebase_init,
        removeScrollability,
        getUserUpdate,
        isJSDateToStringFormat,
        isDDMMYYYYFormat
    }
}

export default useCommon